// Pure helpers for showing which commissioner(s) serve which unit, and which one is the unit's operational commissioner. No Firestore
// here: the screens pass in the relationships they are already authorized to read (firestore.rules scopes them exactly like units) and
// the display names resolved by the Person-label lookup (name only — no contact details).
//
// Two ideas, kept apart on every screen:
//   relationship   a commissioner who serves the unit (Scouting America report, entered by hand, or both). Any number. Informational.
//   operational    the one commissioner Compass uses for unit-scoped access and Action routing (Unit.commissionerPersonId).
import type { CommissionerOrigin, Unit, UnitCommissionerAssignment } from '../types';

export interface CommissionerLine {
  personId: string;
  /** Resolved display name, or null while it loads / when the viewer's lookup returns nothing. */
  name: string | null;
  /** Position names, in report order (e.g. "Unit Commissioner", "District Commissioner"). Empty for a hand designation. */
  positions: string[];
  districtId: string;
  /** ISO date or null. */
  positionExpiry: string | null;
  origins: CommissionerOrigin[];
  /** This person is the unit's operational commissioner. */
  operational: boolean;
}

export const ORIGIN_LABELS: Record<CommissionerOrigin, string> = { manual: 'Assigned in Compass', scoutingAmerica: 'Scouting America' };

/** Origins of a relationship, tolerating one written before `origins` existed (those all came from the report). */
export function originsOf(link: Pick<UnitCommissionerAssignment, 'origins' | 'source'>): CommissionerOrigin[] {
  if (Array.isArray(link.origins)) return link.origins.filter((o): o is CommissionerOrigin => o === 'manual' || o === 'scoutingAmerica');
  return link.source?.startsWith('scouting-america') ? ['scoutingAmerica'] : [];
}

/** Active relationships only; ended ones are history and never count as a commissioner. */
export function activeLinks(links: readonly UnitCommissionerAssignment[]): UnitCommissionerAssignment[] {
  return links.filter((l) => l.status === 'active');
}

export function linksByUnit(links: readonly UnitCommissionerAssignment[]): Map<string, UnitCommissionerAssignment[]> {
  const map = new Map<string, UnitCommissionerAssignment[]>();
  for (const l of activeLinks(links)) {
    const arr = map.get(l.unitId) ?? [];
    arr.push(l);
    map.set(l.unitId, arr);
  }
  return map;
}

/** Catalog position names for a link, falling back to the report's printed text when an id is not in the catalog. */
export function positionNames(link: UnitCommissionerAssignment, catalog: ReadonlyArray<{ positionId: string; name: string }>): string[] {
  const byId = new Map(catalog.map((p) => [p.positionId, p.name]));
  const names = link.positionIds.map((id) => byId.get(id)).filter((n): n is string => !!n);
  return names.length === link.positionIds.length && names.length > 0 ? names : [...link.reportPositions];
}

/** Display lines for one unit: the operational commissioner first, then by name (unresolved names last). A designated operational
 *  commissioner always appears, even when no relationship record exists for them yet (a council not migrated yet). */
export function commissionerLines(
  links: readonly UnitCommissionerAssignment[],
  catalog: ReadonlyArray<{ positionId: string; name: string }>,
  nameOf: (personId: string) => string | null,
  operational: { personId: string | null | undefined; districtId: string } | null = null,
): CommissionerLine[] {
  const opId = operational?.personId || null;
  const lines: CommissionerLine[] = links.map((l) => ({
    personId: l.personId, name: nameOf(l.personId), positions: positionNames(l, catalog), districtId: l.districtId,
    positionExpiry: l.positionExpiry ?? null, origins: originsOf(l), operational: l.personId === opId,
  }));
  if (opId && operational && !lines.some((l) => l.personId === opId)) {
    lines.push({ personId: opId, name: nameOf(opId), positions: [], districtId: operational.districtId, positionExpiry: null, origins: ['manual'], operational: true });
  }
  return lines.sort((a, b) => Number(b.operational) - Number(a.operational) || (a.name ?? '\uffff').localeCompare(b.name ?? '\uffff') || a.personId.localeCompare(b.personId));
}

export interface CommissionerCoverage {
  total: number;
  /** Units with at least one commissioner relationship (operational or associated), each unit counted once. */
  withCommissioner: number;
  without: number;
  /** 0–100, one decimal place; null when there are no units. */
  percent: number | null;
}

/** Coverage over exactly the units passed in (the screen passes the district-filtered list), counting a unit once however many
 *  commissioners serve it. */
export function commissionerCoverage(units: readonly Pick<Unit, 'id'>[], byUnit: ReadonlyMap<string, unknown[]>): CommissionerCoverage {
  const total = units.length;
  const withCommissioner = units.filter((u) => (byUnit.get(u.id)?.length ?? 0) > 0).length;
  return {
    total,
    withCommissioner,
    without: total - withCommissioner,
    percent: total === 0 ? null : Math.round((withCommissioner / total) * 1000) / 10,
  };
}

/** The unit ids that have a commissioner for coverage purposes: every unit with an active relationship, plus every unit that has an
 *  operational commissioner designated (a designation always implies a relationship once migrated; this keeps a not-yet-migrated unit
 *  from showing as uncovered). Returned as a map so `commissionerCoverage` counts each unit once. */
export function coverageByUnit(
  links: readonly UnitCommissionerAssignment[],
  units: ReadonlyArray<Pick<Unit, 'id' | 'commissionerPersonId' | 'commissionerContactId'>>,
): Map<string, unknown[]> {
  const map = new Map<string, unknown[]>(linksByUnit(links));
  for (const u of units) if (!map.has(u.id) && (u.commissionerPersonId || u.commissionerContactId)) map.set(u.id, [u.commissionerPersonId ?? u.commissionerContactId]);
  return map;
}
